const HTML_ENTITY_MAP: Record<string, string> = {
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&amp;': '&',
};

/**
 * Remove artefatos HTML de SQL copiado da UI ou re-highlight acidental.
 */
export function sanitizeSqlInput(sql: string): string {
    if (!sql) return '';

    let cleaned = sql
        .replace(/<span[^>]*>/gi, '')
        .replace(/<\/span>/gi, '')
        .replace(/<\/?(?:pre|code)\b[^>]*>/gi, '');

    for (const [entity, char] of Object.entries(HTML_ENTITY_MAP)) {
        cleaned = cleaned.split(entity).join(char);
    }

    return cleaned.trim();
}

export const SQL_CATEGORIES = {
    keywords: [
        'SELECT', 'FROM', 'WHERE', 'GROUP BY', 'ORDER BY', 'HAVING', 'JOIN', 'LEFT JOIN',
        'RIGHT JOIN', 'INNER JOIN', 'OUTER JOIN', 'ON', 'AS', 'WITH', 'LIMIT', 'OFFSET',
        'DISTINCT', 'UNION', 'UNION ALL'
    ],
    functions: [
        'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COALESCE', 'NULLIF', 'CAST', 'ROUND',
        'DATE_TRUNC', 'TO_CHAR', 'EXTRACT', 'SUBSTRING', 'UPPER', 'LOWER'
    ],
    operators: [
        'AND', 'OR', 'IN', 'NOT IN', 'EXISTS', 'NOT EXISTS', 'LIKE', 'NOT LIKE',
        'BETWEEN', 'NOT BETWEEN', 'IS NULL', 'IS NOT NULL', 'DESC', 'ASC'
    ],
    conditionals: [
        'CASE', 'WHEN', 'THEN', 'ELSE', 'END'
    ]
};

export const formatSQLWithHighlight = (sql: string) => {
    let formattedSQL = sanitizeSqlInput(sql);

    // Escapar caracteres HTML sensíveis ANTES de qualquer destaque
    formattedSQL = formattedSQL.replace(/&/g, '&amp;');
    formattedSQL = formattedSQL.replace(/</g, '&lt;');
    formattedSQL = formattedSQL.replace(/>/g, '&gt;');

    // Função auxiliar para escapar caracteres especiais do regex
    const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Criar regex para cada categoria
    const createCategoryRegex = (keywords: string[]) => {
        const escaped = keywords.map(escapeRegex);
        return new RegExp(`\\b(${escaped.join('|')})\\b`, 'gi');
    };

    // Array para armazenar placeholders temporários
    const placeholders: string[] = [];
    let placeholderIndex = 0;

    // Função para criar placeholder único
    const createPlaceholder = (content: string) => {
        const placeholder = `__PLACEHOLDER_${placeholderIndex}__`;
        placeholders[placeholderIndex] = content;
        placeholderIndex++;
        return placeholder;
    };

    // Função para restaurar placeholders
    const restorePlaceholders = (text: string) => {
        let result = text;
        for (let i = 0; i < placeholders.length; i++) {
            result = result.replace(`__PLACEHOLDER_${i}__`, placeholders[i]);
        }
        return result;
    };

    // 1. Substituir comentários e proteger com placeholder
    formattedSQL = formattedSQL.replace(
        /(--[^\n]*)/g,
        (match, comment) => createPlaceholder(`<span class="text-slate-400">${comment}</span>`)
    );

    // 2. Substituir strings literais (aspas simples) e proteger com placeholder
    formattedSQL = formattedSQL.replace(
        /'([^']*?)'/g,
        (match) => createPlaceholder(`<span class="text-accent">${match}</span>`)
    );

    // 2b. Identificadores PostgreSQL entre aspas duplas — evita highlight de números
    // dentro de "t_400" ou quebra de HTML em "400"
    formattedSQL = formattedSQL.replace(
        /"([^"]*?)"/g,
        (match) => createPlaceholder(`<span class="text-accent">${match}</span>`)
    );

    // 3. Substituir números (agora não vai interferir com as classes CSS)
    formattedSQL = formattedSQL.replace(
        /\b(\d+(\.\d+)?)\b/g,
        '<span class="text-yellow-400">$1</span>'
    );

    // 4. Substituir palavras-chave por categoria
    formattedSQL = formattedSQL.replace(
        createCategoryRegex(SQL_CATEGORIES.keywords),
        '<span class="text-fuchsia-400 font-semibold">$1</span>'
    );

    formattedSQL = formattedSQL.replace(
        createCategoryRegex(SQL_CATEGORIES.functions),
        '<span class="text-cyan-400 font-semibold">$1</span>'
    );

    formattedSQL = formattedSQL.replace(
        createCategoryRegex(SQL_CATEGORIES.operators),
        '<span class="text-orange-400">$1</span>'
    );

    formattedSQL = formattedSQL.replace(
        createCategoryRegex(SQL_CATEGORIES.conditionals),
        '<span class="text-purple-400 font-semibold">$1</span>'
    );

    // 5. Restaurar placeholders (comentários e strings)
    formattedSQL = restorePlaceholders(formattedSQL);

    return formattedSQL;
};
